import React, { useEffect, useState } from "react";
import useMyCalendar from "../useMyCalendar";
import { Skeleton } from "primereact/skeleton";
import Timelines from "../../../components/Timeline";
import StatusBadge from "../../Process/components/StatusBadge";
import styles from "../my-calendar.module.scss";
import { Tooltip } from "primereact/tooltip";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import PropTypes from "prop-types";
import CustomeButton from "../../../components/Button";
import { Badge } from "primereact/badge";
import { toZonedTime } from "date-fns-tz";
import UserTimezone from "./UserTimezone";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import useDateTime from "~/hooks/useDateTime";

const EventDetail = ({ eventDetail }) => {
  const { fetchMilestonesSiblings, milestonesLoading } = useMyCalendar();
  const { timezone } = useSelector((state) => state.login);
  const [milestones, setMilestones] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedServiceLine, setSelectedServiceLine] = useState(null);
  const [recurrence, setRecurrence] = useState(null);
  const [deadlineTime, setDeadlineTime] = useState(null);
  const { formatDateTime } = useDateTime();

  useEffect(() => {
    const getMilestones = async () => {
      const data = await fetchMilestonesSiblings(eventDetail?.id);
      const updatedData = data?.data?.map?.((milestone) => ({
        ...milestone,
        due_date: toZonedTime(
          milestone.due_date,
          eventDetail?.entityData?.timezone,
        ),
      }));
      setMilestones(updatedData);
    };
    getMilestones();
    setSelectedServiceLine(eventDetail?.label);
    setRecurrence({ name: "Daily", code: "Daily" });
    setSelectedStatus(eventDetail?.status);
  }, []);

  const formatMilestoneDate = (dateString) => {
    if (dateString) {
      return formatDateTime({
        date: dateString,
        dateformat: "MMMM do, yyyy",
      });
    } else {
      ("-");
    }
  };

  const viewProcessHandler = () => {
    window.open(
      window.location.origin +
        "/process/" +
        eventDetail?.process +
        "/milestones",
    );
  };

  const statusOption = [
    {
      name: "In Progress",
      code: "in-progress",
      primary_color: "#326FD1",
      secondary_color: null,
    },
    {
      name: "Completed",
      code: "completed",
      primary_color: "#188A42",
      secondary_color: null,
    },
    {
      name: "Overdue",
      code: "overdue",
      primary_color: "#EAB308",
      secondary_color: null,
    },
    {
      name: "Not Required",
      code: "not-needed",
      primary_color: "#9E9E9E",
      secondary_color: null,
    },
    {
      name: "Not Started",
      code: "not-started",
      primary_color: "#757575",
      secondary_color: null,
    },
  ];

  const recurrenceOptions = [
    { name: "Daily", code: "Daily" },
    { name: "Weekly", code: "Weekly" },
    { name: "Monthly", code: "Monthly" },
    { name: "Quarterly", code: "Quarterly" },
    { name: "Yearly", code: "Yearly" },
    { name: "Fortnightly", code: "Fortnightly" },
    { name: "Bi-monthly", code: "Bi-monthly" },
  ];

  const timelineMarkerTemplate = (item) => {
    return !milestonesLoading ? (
      <StatusBadge status={item?.statusData} />
    ) : (
      <Skeleton width="20px" height="20px" shape="circle" />
    );
  };

  const timelineContentTemplate = (item) => {
    return (
      <div className="sidebar-timeline-content-template">
        <p className="sidebar-timeline-content-label">
          {item?.name}{" "}
          {item.numberof_flagged_tasks > 0 && (
            <i className="pi pi-flag-fill text-warning ml-2 flag-red-icon"></i>
          )}
        </p>
        <p className="sidebar-timeline-content-status">{`(${item?.statusData?.name})`}</p>
        <p className="sidebar-timeline-content-date">
          {formatMilestoneDate(item?.due_date)}
        </p>
      </div>
    );
  };

  const getDateInEntityTimezone = (date) => {
    const utcDate = new Date(date);
    const timeZone = eventDetail?.entityData?.timezone;
    const zonedDate = toZonedTime(utcDate, timeZone);
    setDeadlineTime(zonedDate);
  };

  useEffect(() => {
    getDateInEntityTimezone(eventDetail?.due_date_utc);
  }, [eventDetail, eventDetail?.entityData]);

  const getDeadlineTimeValue = () => {
    return `${format(deadlineTime, "hh:mm a")} | ${eventDetail?.entityData?.timezone}`;
  };
  return (
    <div className={styles["sidenav-process"]}>
      <div className="w-full">
        <div className="sidebar-milestones-container">
          <div className={styles["details"]}>
            <p className={styles["sidebar-details-label"]}>Details</p>

            <div className={styles["field"]}>
              <label className="input-label">Status</label>
              <Dropdown
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.value)}
                options={statusOption}
                optionLabel="name"
                disabled
                placeholder="Select status"
                className="appearance-none outline-none w-full"
                itemTemplate={(option) => (
                  <div className="flex align-items-center">
                    <Badge
                      style={{
                        width: "14px",
                        height: "14px",
                        backgroundColor: option?.primary_color,
                      }}
                    />
                    <span>{option?.name}</span>
                  </div>
                )}
                valueTemplate={(option) => (
                  <div className="flex align-items-center">
                    <Badge
                      style={{
                        width: "14px",
                        height: "14px",
                        backgroundColor: option?.primary_color,
                      }}
                    />
                    <span>{option?.name}</span>
                  </div>
                )}
              />
            </div>

            <div className={styles["field"]}>
              <label className="input-label">
                Due Date
                <span
                  className="info-icon ml-1 pi pi-info-circle"
                  id="due_date"
                />
                <Tooltip
                  target="#due_date"
                  content="This date and time are based on the entity's time zone, which may differ from your local time."
                  position="bottom"
                  className="calendar-tooltip"
                />
              </label>
              <InputText
                value={formatDateTime({
                  date: deadlineTime,
                  dateformat: "MMMM do, yyyy",
                })}
                readOnly
                className="w-full request__calander__input"
                placeholder="Select date"
                disabled
              />
              <span
                className={styles["sidenav-input-icon-date"]}
                data-testid="sidenav-input-icon"
              >
                <i className="pi pi-calendar"></i>
              </span>
            </div>
            <div className={styles["field"]}>
              <InputText
                value={getDeadlineTimeValue()}
                placeholder="Select time"
                className="w-full"
                data-testid="select-time"
                readOnlyInput
                disabled
                onChange={(e) => {
                  const isoDate = e.value ? new Date(e.value) : "";
                  setDeadlineTime(isoDate);
                }}
              />
              <span
                className={styles["sidenav-input-icon"]}
                data-testid="sidenav-input-icon"
              >
                <i className="pi pi-clock"></i>
              </span>
              <UserTimezone dueDate={eventDetail.end} timezone={timezone} />
            </div>
            <div className={styles["field"]}>
              <label className="input-label">Entity Name</label>
              <InputText
                value={eventDetail?.entityData?.name}
                placeholder="Select Entity Name"
                name="entity"
                className={`appearance-none outline-none w-full ${styles["truncate-value"]}`}
                disabled
              />
              <span
                className={styles["sidenav-input-icon-lock"]}
                data-testid="sidenav-input-icon-lock"
              >
                <i className="pi pi-lock"></i>
              </span>
            </div>
            <div className={styles["field"]}>
              <label className="input-label">Service Line</label>
              <InputText
                value={selectedServiceLine}
                placeholder="Select Service Line"
                name="email"
                className="appearance-none outline-none w-full "
                disabled
              />
              <span
                className={styles["sidenav-input-icon-lock"]}
                data-testid="sidenav-input-icon-lock"
              >
                <i className="pi pi-lock"></i>
              </span>
            </div>
            <div className={styles["field"]}>
              <label className="input-label">Process Name</label>
              <InputText
                value={eventDetail?.processData?.name}
                placeholder="Select Process Name"
                name="process"
                className={`appearance-none outline-none w-full ${styles["truncate-value"]}`}
                disabled
              />
              <span
                className={styles["sidenav-input-icon-lock"]}
                data-testid="sidenav-input-icon-lock"
              >
                <i className="pi pi-lock"></i>
              </span>
            </div>

            <div className={styles["action-buttons"]}>
              <CustomeButton
                label="View Process"
                variant="contained"
                icon="pi pi-external-link"
                iconPos="right"
                onClick={viewProcessHandler}
              />
            </div>
          </div>
          <div className="milestones">
            <p className={styles["sidebar-milestones-label"]}>
              Parent Process Milestones
            </p>
            <div className="sidebar-timeline-container">
              {milestonesLoading ? (
                <div className="sidebar-timeline-content-template">
                  <Skeleton width="100px" height="20px" className="mr-1" />
                  <Skeleton width="70px" height="10px" className="mr-1" />
                  <Skeleton width="100px" height="10px" className="mr-1" />
                </div>
              ) : (
                <Timelines
                  events={milestones}
                  markerTemplate={timelineMarkerTemplate}
                  contentTemplate={timelineContentTemplate}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

EventDetail.propTypes = {
  eventDetail: PropTypes.object,
};

export default EventDetail;
